import { Model, type ModelProps } from '@/infra/model'
import { observable } from 'mobx'
import { Job, type Resolution } from './Job'
import { Member } from '@/features/iam/members/Member'
import { unixEpoch } from '@/utils/date-util'
import { Id } from '@/infra/id'

/**
 * What happened to a job.
 */
export type JobEventType =
  | 'Unspecified'
  | 'Unclaimed'
  | 'Claimed'
  | 'Updated'
  | 'Amended'
  | 'Reopened'
  | 'Transferred'
  | 'Forfeited'
  | 'ProviderAdded'
  | 'ProviderRemoved'
  | 'JobMemberAdded'
  | 'JobMemberRemoved'
  | 'TaskMarkedComplete'
  | 'TaskMarkedIncomplete'
  | 'Closed'
  | 'Resumed'
  | 'ResumedJob'
  | 'PutOnHold'
  | 'BecameInactive'
  | 'PaymentInfoRequested'
  | 'PaymentInfoProvided'
  | 'PaymentSuccessful'
  | 'PaymentFailed'
  | 'CouponAdded'
  | 'CouponRemoved'
  | 'RefundSuccessful'
  | 'RefundFailed'
  | 'ManualInvoiceAdded'
  | 'AnswersCorrected'
  | 'InfoGatheringCompleted'
  | 'ClientAdded'

/**
 * Who can see a job event.
 */
export type JobEventVisibility = 'Public' | 'Internal'

/**
 * Props of a job event.
 */
export type JobEventProps = ModelProps<JobEvent>

/**
 * Something that happened to a job, as shown in its history.
 */
export class JobEvent extends Model<JobEvent> {
  id = Id.none

  @observable accessor job = Job.none
  @observable accessor eventType: JobEventType = 'Updated'
  @observable accessor eventVisibility: JobEventVisibility = 'Internal'
  @observable accessor description = ''
  @observable accessor dateCreated: Date = unixEpoch()
  @observable accessor triggeredBy: Member = Member.none
  @observable accessor reason = ''
  @observable accessor taskTitle: string | undefined = undefined
  @observable accessor previousProvider: string | undefined = undefined
  @observable accessor newTeam: string | undefined = undefined
  @observable accessor jobResolution: Resolution | undefined = undefined

  @observable accessor newProvider: string | undefined = undefined
  @observable accessor newClient: string | undefined = undefined
  @observable accessor provider: string | undefined = undefined
}
